import React, { useState, useEffect, useRef } from 'react';
import { ClipboardCheck } from 'lucide-react';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { pas2030QuestionsSchema } from '../../schemas/pas2030QuestionsSchema';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface PAS2030QuestionsFormSchemaVersionProps {
  surveyData: any;
  onDataUpdate: (updater: (prev: any) => any, section: string) => void;
  customFields: FieldSchema[];
  hiddenFields: string[];
  hiddenSections: string[];
  sectionOrder: string[];
  builtInFieldOrder: string[];
  onNavigateToPrevious?: () => void;
  onNavigateToNext?: () => void;
}

export const PAS2030QuestionsFormSchemaVersion: React.FC<PAS2030QuestionsFormSchemaVersionProps> = ({
  surveyData,
  onDataUpdate,
  customFields,
  hiddenFields,
  hiddenSections,
  sectionOrder,
  builtInFieldOrder,
  onNavigateToPrevious,
  onNavigateToNext,
}) => {
  const surveyId = surveyData?.id;
  
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<Record<string, any[]>>({});
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  
  const isInternalUpdate = useRef(false);
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(surveyId, propertyName);

  // Determine which sections to show based on measure_specification data
  const selectedMeasures = surveyData?.measure_specification?.suitableMeasures || [];
  const wallInsulationType = surveyData?.measure_specification?.wallInsulationType || '';
  
  const showHeating = selectedMeasures.includes('Heating System');
  const showWallInsulation = selectedMeasures.includes('Wall Insulation');
  const showRIR = selectedMeasures.includes('RIR insulation');
  
  // Determine wall insulation type
  const wallTypeLower = wallInsulationType.toLowerCase();
  let dynamicHiddenSections = [...hiddenSections];
  
  // Hide sections that aren't applicable
  if (!showHeating) {
    dynamicHiddenSections.push('heating_system');
  }
  
  if (!showWallInsulation) {
    dynamicHiddenSections.push('iwi_questions', 'ewi_questions', 'cwi_questions');
  } else {
    // Hide wall insulation sections that don't match the selected type
    if (!wallTypeLower.includes('iwi') && !wallTypeLower.includes('internal')) {
      dynamicHiddenSections.push('iwi_questions');
    }
    if (!wallTypeLower.includes('ewi') && !wallTypeLower.includes('external')) {
      dynamicHiddenSections.push('ewi_questions');
    }
    if (!wallTypeLower.includes('cwi') && !wallTypeLower.includes('cavity')) {
      dynamicHiddenSections.push('cwi_questions');
    }
  }
  
  if (!showRIR) {
    dynamicHiddenSections.push('rir_questions');
  }

  // Load built-in form data from measure_specification.pas2030Questions
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PAS2030Questions] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.measure_specification?.pas2030Questions) {
      console.log('[PAS2030Questions] Loading built-in data from surveyData');
      const questionsData = surveyData.measure_specification.pas2030Questions;
      
      const newFormData: Record<string, any> = {};
      const newPhotos: Record<string, any[]> = {};
      
      // Flatten nested structure (heatingSystem, iwi, ewi, cwi, rirInsulation)
      Object.entries(questionsData).forEach(([section, sectionData]: [string, any]) => {
        if (typeof sectionData === 'object' && sectionData !== null) {
          Object.entries(sectionData).forEach(([key, value]) => {
            const fieldId = `${section}_${key}`;
            
            const schemaField = pas2030QuestionsSchema.sections
              .flatMap(s => s.fields)
              .find(f => f.id === fieldId);
            
            if (schemaField?.type === 'photo' && Array.isArray(value)) {
              newPhotos[fieldId] = value;
            } else if (value !== undefined && value !== null) {
              newFormData[fieldId] = value;
            }
          });
        }
      });
      
      setFormData(newFormData);
      setPhotos(newPhotos);
    }
  }, [surveyData]);
  
  // Load custom field data
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PAS2030Questions] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.measure_specification?.pas2030Questions && customFields && customFields.length > 0) {
      console.log('[PAS2030Questions] Loading custom field data');
      const existingData: Record<string, any> = {};
      const questionsData = surveyData.measure_specification.pas2030Questions;
      
      customFields.forEach(field => {
        const value = (questionsData as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
        
        const photosFieldId = `${field.id}_photos`;
        const photoValue = (questionsData as any)?.[photosFieldId];
        if (photoValue !== undefined) {
          console.log('[PAS2030Questions] Loading photos for custom field:', field.id, '→', photosFieldId);
          existingData[photosFieldId] = photoValue;
        }
      });
      
      if (Object.keys(existingData).length > 0) {
        console.log('[PAS2030Questions] Loaded custom field data:', Object.keys(existingData));
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.measure_specification?.pas2030Questions, customFields]);
  
  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PAS2030Questions] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);
  
  // Handle form data changes - save to measure_specification.pas2030Questions
  const handleFormDataChange = (fieldId: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          // Convert flat structure back to nested for storage
          const nestedData = convertToNestedStructure(updated);
          
          onDataUpdate((prevData) => ({
            ...prevData,
            measure_specification: {
              ...prevData.measure_specification,
              pas2030Questions: {
                ...prevData.measure_specification?.pas2030Questions,
                ...nestedData
              }
            }
          }), 'measure_specification');
        }, 50);
      }
      
      return updated;
    });
  };
  
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            measure_specification: {
              ...prevData.measure_specification,
              pas2030Questions: {
                ...prevData.measure_specification?.pas2030Questions,
                ...updated
              }
            }
          }), 'measure_specification');
        }, 50);
      }
      
      return updated;
    });
  };
  
  // Convert flat field structure to nested (heatingSystem.buildingFabric, etc.)
  const convertToNestedStructure = (flatData: Record<string, any>) => {
    const nested: Record<string, Record<string, any>> = {
      heatingSystem: {},
      iwi: {},
      ewi: {},
      cwi: {},
      rirInsulation: {}
    };
    
    Object.entries(flatData).forEach(([fieldId, value]) => {
      const parts = fieldId.split('_');
      if (parts.length >= 2) {
        const section = parts[0];
        const key = parts.slice(1).join('_');
        
        if (nested[section]) {
          nested[section][key] = value;
        }
      }
    });
    
    return nested;
  };
  
  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    const newPhotoData = newPhotos.map(photo => {
      let dataUrl = photo.dataUrl || photo.base64String;
      
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      return {
        id: photo.id || `photo_${Date.now()}_${Math.random()}`,
        displayId: photo.displayId || photo.id,
        dataUrl,
        url: photo.url || '',
        uploading: false,
        format: photo.format || 'jpeg',
      };
    });
    
    setPhotos(prev => {
      const updated = { ...prev, [fieldId]: newPhotoData };
      
      if (onDataUpdate) {
        isInternalUpdate.current = true;
        onDataUpdate((prevData) => ({
          ...prevData,
          measure_specification: {
            ...prevData.measure_specification,
            pas2030Questions: {
              ...prevData.measure_specification?.pas2030Questions,
              [fieldId]: newPhotoData
            }
          }
        }), 'measure_specification');
      }
      
      return updated;
    });
    
    newPhotoData.forEach((photo) => {
      if (photo.dataUrl && !photo.url) {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'measure_specification',
          fieldId,
          (url: string) => {
            setPhotos(prev => ({
              ...prev,
              [fieldId]: (prev[fieldId] || []).map(p =>
                p.id === photo.id ? { ...p, url, uploading: false } : p
              ),
            }));
          },
          (error: string) => console.error('Photo upload failed:', error)
        );
      }
    });
  };
  
  const handleGalleryOpen = (fieldId: string) => {
    setSelectedGallery(fieldId);
  };
  
  const handleGalleryClose = () => {
    setSelectedGallery(null);
  };

  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => (p.displayId || p.id) === displayIdToRemove);
    if (photoToDelete?.fileName || photoToDelete?.url) {
      let filePath = photoToDelete.fileName || photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
      if (filePath) try { await deletePhotoFromSupabase(filePath, 'retrofit-survey-images'); } catch (e) {}
    }
    const filteredPhotos = currentPhotos.filter((p: any) => (p.displayId || p.id) !== displayIdToRemove);
    setPhotos(prev => ({ ...prev, [photoType]: filteredPhotos }));
    if (onDataUpdate) {
      onDataUpdate((prevData) => ({
        ...prevData,
        measure_specification: {
          ...prevData.measure_specification,
          pas2030Questions: {
            ...prevData.measure_specification?.pas2030Questions,
            [photoType]: filteredPhotos
          }
        }
      }), 'measure_specification');
    }
  };

  const handleSubmit = (data: Record<string, any>) => {
    console.log('[PAS2030Questions] Form submitted:', data);
  };
  
  const allFormData = { ...formData, ...customFieldData };

  return (
    <>
      <BuiltInFormRenderer
        schema={pas2030QuestionsSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          const baseFieldId = fieldId.replace(/_photos$/, '');
          const isCustomField = customFields.some(f => f.id === baseFieldId);
          
          if (isCustomField) {
            handleCustomFieldChange(fieldId, value);
          } else {
            handleFormDataChange(fieldId, value);
          }
        }}
        onPhotosChange={handlePhotosChange}
        onSubmit={handleSubmit}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={dynamicHiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: onNavigateToPrevious ? {
            label: '← Previous',
            onClick: onNavigateToPrevious
          } : undefined,
          next: onNavigateToNext ? {
            label: 'Next →',
            onClick: onNavigateToNext
          } : undefined
        }}
        onGalleryOpen={handleGalleryOpen}
        selectedGallery={selectedGallery}
        formIcon={<ClipboardCheck className="w-8 h-8" />}
      />
      
      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId || photo.id,
            url: photo.url || photo.dataUrl,
            uploading: photo.uploading,
            error: photo.error
          }))}
          sectionTitle={`${selectedGallery} Photos`}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(current => ({
              ...current,
              [selectedGallery]: current[selectedGallery].map(p =>
                (p.displayId || p.id) === photoId ? { ...p, note } : p
              )
            }));
            
            if (onDataUpdate) {
              onDataUpdate((prevData) => ({
                ...prevData,
                measure_specification: {
                  ...prevData.measure_specification,
                  pas2030Questions: {
                    ...prevData.measure_specification?.pas2030Questions,
                    [selectedGallery]: (photos[selectedGallery] || []).map(p =>
                      (p.displayId || p.id) === photoId ? { ...p, note } : p
                    )
                  }
                }
              }), 'measure_specification');
            }
          }}
        />
      )}
    </>
  );
};
